// Programmatic SEO — Country × Service pair page builder.
// Combines CountryHub data with CoreServicePillar data to render a unique,
// long-form page per (country, service) pair without hand-writing each.
//
// Curated to ~6 core services per country = ~72 pages total.
// Each page targets long-tail queries like
//   "notary public Phuket for UK", "police clearance for Germany visa Phuket",
//   "MFA Apostille for Japan from Phuket", etc.

import { COUNTRIES, getCountryBySlug } from "@/content/countries/registry";
import type { CountryHub } from "@/content/countries/types";
import { getCoreServicePillar, type CoreServicePillar } from "@/content/service-pillars/core.gen";

/**
 * Core services offered programmatically for every country.
 * Keep tight — we add per-country marriage + visa slugs on top.
 */
export const CORE_PROGRAMMATIC_SERVICES = [
  "notary-public-english",
  "translation-certified-all",
  "mfa-consular",
  "embassy-legalization",
  "police-clearance-intl",
] as const;

export interface CountryServicePair {
  country: CountryHub;
  service: CoreServicePillar;
}

/** All valid (country, service) slug pairs. */
export function listAllPairs(): { countrySlug: string; serviceSlug: string }[] {
  const out: { countrySlug: string; serviceSlug: string }[] = [];
  for (const c of COUNTRIES) {
    const slugs = new Set<string>(CORE_PROGRAMMATIC_SERVICES);
    if (c.marriageServiceSlug) slugs.add(c.marriageServiceSlug);
    if (c.visaServiceSlug) slugs.add(c.visaServiceSlug);
    for (const s of slugs) {
      if (getCoreServicePillar(s)) {
        out.push({ countrySlug: c.slug, serviceSlug: s });
      }
    }
  }
  return out;
}

/** Resolve a pair from URL params. Returns null when invalid. */
export function resolvePair(
  countrySlug: string,
  serviceSlug: string,
): CountryServicePair | null {
  const country = getCountryBySlug(countrySlug);
  if (!country) return null;
  const service = getCoreServicePillar(serviceSlug);
  if (!service) return null;
  // Only allow whitelisted pairs (avoid combinatorial explosion / thin content).
  const allowed = new Set<string>(CORE_PROGRAMMATIC_SERVICES);
  if (country.marriageServiceSlug) allowed.add(country.marriageServiceSlug);
  if (country.visaServiceSlug) allowed.add(country.visaServiceSlug);
  if (!allowed.has(serviceSlug)) return null;
  return { country, service };
}

/** Display metadata & body for one pair (used by route head + render). */
export interface PairContent {
  h1: string;
  metaTitle: string;
  metaDesc: string;
  tldr: string;
  quickFacts: { label: string; value: string }[];
  whyThisCountry: string[];
  process: { step: number; title: string; body: string }[];
  documents: string[];
  faqs: { q: string; a: string }[];
  related: { slug: string; label: string }[];
  finalCta: string;
}

function categoryLabelTh(cat: CoreServicePillar["category"]): string {
  switch (cat) {
    case "notary":
      return "Notary Public";
    case "translation":
      return "แปลและรับรอง";
    case "legalization":
      return "MFA & Embassy";
    case "background":
      return "ตรวจประวัติ";
    case "visa-marriage":
      return "วีซ่า & สมรส";
    default:
      return "บริการ";
  }
}

function shortServiceTitle(s: CoreServicePillar): string {
  // Keep meta title under ~60 chars; pillar h1 is usually long.
  return s.metaTitle.replace(/\s*\|\s*Thai Notary.*/i, "").trim();
}

export function buildPairContent({
  country,
  service,
}: CountryServicePair): PairContent {
  const apostilleNote = country.isApostille
    ? `${country.nameTh} เป็นสมาชิกอนุสัญญากรุงเฮก — เอกสารไทยใช้ตรา Apostille ของ MFA ได้เลย ไม่ต้องไปรับรองที่สถานทูต ${country.nameEn} ในกรุงเทพอีกขั้น`
    : `${country.nameTh} ยังไม่ได้เป็นสมาชิก Apostille — เอกสารทุกฉบับต้องผ่าน MFA Consular Legalization ก่อน แล้วต่อด้วยการรับรองที่สถานทูต ${country.nameEn} กรุงเทพ จึงจะใช้งานได้`;

  const langNote = `เอกสารที่ใช้ใน ${country.nameTh} ส่วนใหญ่ต้องแปลเป็นภาษา${country.language} โดยนักแปลที่ทางการ ${country.nameEn} ยอมรับ — เรามีนักแปลในทีมที่ส่งงานให้ทันที พร้อมแนบ Translation Certificate ทุกชุด`;

  const baseMeta = `${shortServiceTitle(service)} สำหรับใช้ที่ ${country.nameTh} ${country.flag}`;
  const rawTitle = `${baseMeta} — ภูเก็ต`;
  const metaTitle = rawTitle.length > 60 ? rawTitle.slice(0, 59) + "…" : rawTitle;
  const rawDesc = `${categoryLabelTh(service.category)}สำหรับ ${country.nameTh} (${country.nameEn}) จากภูเก็ต — ${country.isApostille ? "ใช้ Apostille MFA" : "ผ่าน MFA + สถานทูต"} ระยะเวลา ${country.timeline} เริ่ม ${service.pricing[0]?.price ?? "ตามใบเสนอราคา"}.`;
  const metaDesc =
    rawDesc.length > 158
      ? rawDesc.slice(0, 157) + "…"
      : rawDesc.length < 120
        ? (rawDesc + ` ทีม Notarial Services Attorney + นักแปลภาษา${country.language} ดูแลครบจบ.`).slice(0, 158)
        : rawDesc;

  const h1 = `${shortServiceTitle(service)} สำหรับใช้ที่ ${country.nameTh} ${country.flag} — เริ่มต้นจากภูเก็ต`;

  const tldr = `ต้องใช้ ${shortServiceTitle(service)} สำหรับ ${country.nameTh}? ทีม Thai Notary Phuket ดูแลครบจบในที่เดียว — ${apostilleNote.replace(/—.+$/, "")}. ราคาเริ่มต้น ${service.pricing[0]?.price ?? "ตามใบเสนอราคา"} • ระยะเวลา ${country.timeline} • รองรับเอกสารใน${country.language}.`;

  const quickFacts = [
    { label: "ปลายทาง", value: `${country.flag} ${country.nameTh} (${country.nameEn})` },
    { label: "ภูมิภาค", value: country.region },
    { label: "ระบบรับรอง", value: country.isApostille ? "Apostille (Hague 1961)" : "Consular Legalization (Non-Apostille)" },
    { label: "ภาษาเอกสาร", value: country.language },
    { label: "ระยะเวลาทั้งกระบวนการ", value: country.timeline },
    { label: "งบประมาณรวม", value: country.costRange },
  ];

  const whyThisCountry = [
    `เราดูแลเคสไป ${country.nameTh} เป็นประจำ — ทีมรู้ขั้นตอนของหน่วยงานปลายทางและของสถานทูต ${country.nameEn} ในกรุงเทพดีกว่าทนายทั่วไป`,
    apostilleNote,
    langNote,
    `ทุกขั้นตอนเริ่มต้นและจบที่ภูเก็ต — ลูกค้าไม่ต้องเดินทางไปกรุงเทพเอง เราเป็นตัวกลางทั้ง MFA ${country.isApostille ? "Apostille" : "Legalization"} และสถานทูต`,
  ];

  const process: PairContent["process"] = [
    {
      step: 1,
      title: "ส่งเอกสารตรวจสอบฟรี",
      body: `ถ่ายภาพ/สแกนส่งทางไลน์ หรือนัดเข้าออฟฟิศภูเก็ต ทีมตรวจว่าเอกสารตรงตามที่หน่วยงานใน ${country.nameTh} กำหนดหรือไม่ ภายใน 30 นาที`,
    },
    {
      step: 2,
      title: `แปลเป็นภาษา${country.language}`,
      body: `แปลโดยนักแปลที่ ${country.nameEn} ยอมรับ พร้อม Translation Certificate ของบริษัทแนบทุกชุด`,
    },
    {
      step: 3,
      title: service.category === "notary" ? "รับรองโดยทนาย Notarial Services Attorney" : "ตรวจสอบและจัดชุดเอกสาร",
      body: `จัดเอกสารตามรูปแบบที่ ${country.nameEn} ${service.category === "notary" ? "หรือสถานทูต" : ""} ต้องการ — ทุกชิ้นเซ็นโดยทนายที่ขึ้นทะเบียนสภาทนายความฯ`,
    },
    {
      step: 4,
      title: country.isApostille ? "MFA Apostille (กรุงเทพ)" : "MFA Consular Legalization (กรุงเทพ)",
      body: country.isApostille
        ? `นำเอกสารไปกรมการกงสุล รับตรา Apostille — ทีมเดินทางให้ ลูกค้าไม่ต้องไปเอง`
        : `รับรองสองชั้น: MFA + สถานทูต ${country.nameEn} — ทีมจัดคิวและเดินเอกสารให้ครบจบ`,
    },
    {
      step: 5,
      title: country.isApostille ? "ส่งคืน / EMS ทั่วโลก" : `รับรองสถานทูต ${country.nameEn}`,
      body: country.isApostille
        ? `จัดส่งคืนภูเก็ตหรือส่ง EMS/DHL ตรงไป ${country.nameTh} ได้`
        : `นำเอกสารยื่นที่สถานทูต ${country.nameEn} กรุงเทพ รอรับ 3–7 วันทำการ`,
    },
  ];

  const documents = [
    ...country.documentsRequired,
    "Passport + สำเนา (เซ็นรับรอง)",
    "เอกสารเสริมตามประเภทบริการ — ทีมแจ้งรายการเฉพาะหลังตรวจเคส",
  ];

  const faqs: { q: string; a: string }[] = [
    {
      q: `${shortServiceTitle(service)} สำหรับ ${country.nameTh} ใช้เวลานานเท่าไร?`,
      a: `ปกติทั้งกระบวนการ (แปล + รับรอง + MFA${country.isApostille ? " Apostille" : " + สถานทูต"}) อยู่ที่ ${country.timeline} ขึ้นอยู่กับจำนวนเอกสารและคิวของหน่วยงาน เร่งด่วนได้ในบางกรณี`,
    },
    {
      q: `งบประมาณรวมโดยประมาณเท่าไร?`,
      a: `${country.costRange} (ราคารวม Notary + แปล + ${country.isApostille ? "Apostille" : "MFA + Embassy"} ยังไม่รวมค่าธรรมเนียมราชการที่แต่ละหน่วยงานเรียกเก็บ)`,
    },
    {
      q: `อยู่ภูเก็ต ทำได้เลยไหม ไม่ต้องบินไปกรุงเทพ?`,
      a: `ได้ครับ — ลูกค้าเซ็นเอกสารต่อหน้าทนายที่ออฟฟิศภูเก็ต หรือนัด on-site ที่บ้าน/โรงแรม ส่วน MFA และสถานทูตในกรุงเทพ ทีมเดินเอกสารให้ทั้งหมด`,
    },
    ...country.faqs.slice(0, 2),
    ...service.faqs.slice(0, 2),
  ];

  const related: PairContent["related"] = [];
  for (const s of country.relatedServiceSlugs) {
    if (s === service.slug) continue;
    const sv = getCoreServicePillar(s);
    if (sv) related.push({ slug: s, label: shortServiceTitle(sv) });
    if (related.length >= 4) break;
  }

  const finalCta = `พร้อมเริ่มเคส ${shortServiceTitle(service)} สำหรับ ${country.nameTh} แล้วหรือยัง? ทักไลน์ส่งเอกสารให้ทีมประเมิน ภายใน 30 นาทีรับใบเสนอราคา และเริ่มงานในวันเดียวกันได้.`;

  return {
    h1,
    metaTitle,
    metaDesc,
    tldr,
    quickFacts,
    whyThisCountry,
    process,
    documents,
    faqs,
    related,
    finalCta,
  };
}